import { useState } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ResultViewer } from "@/pages/POSRetail";
import LogsPanel from "@/components/LogsPanel";
import BackupPanel from "@/components/BackupPanel";
import DbHealthDialog from "@/components/DbHealthDialog";
import PrintHealthPanel from "@/components/PrintHealthPanel";
import PrintTemplatesPanel from "@/components/PrintTemplatesPanel";
import SettingsPage from "@/pages/Settings";
import { toast } from "sonner";
import { Send, ShieldCheck, ChevronDown, ChevronRight, FileText, SlidersHorizontal, Printer, Database, Terminal } from "lucide-react";

/**
 * Tenant «Διαχείριση» page.
 *
 * Since Feb 2026 the following administrative surfaces have been moved
 * to the Supervisor Panel (per-tenant) and are no longer accessible
 * from here:
 *   • Ενεργά Modules
 *   • Κωδικοί Ασφαλείας (SECURITY|DEVICE|APP pass)
 *   • Εισαγωγή / Εξαγωγή Πινάκων
 *   • Διαχείριση Πινάκων (bulk delete / truncate)
 *
 * ``Backup`` and ``Έλεγχος Υγείας Δεδομένων`` remain here on top of
 * being surfaced in the Supervisor panel, since customers still need
 * day-to-day access to them (per user requirement).
 */
export default function Admin() {
  const [raw, setRaw] = useState("STATUS");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [healthOpen, setHealthOpen] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);
  // Ιούν 2026: top-level ενότητες. Πριν, τα κοινά εργαλεία (Πρότυπα
  // Εκτυπώσεων, Backup, Logs κλπ) ήταν πάντα ορατά ΚΑΤΩ από τα tabs των
  // ρυθμίσεων, οπότε φαινόταν να «επαναλαμβάνονται» σε κάθε υπομενού.
  // Τώρα κάθε ενότητα δείχνεται ξεχωριστά — μία κάθε φορά.
  const [section, setSection] = useState("settings");

  const SECTIONS = [
    { key: "settings", label: "Ρυθμίσεις Συστήματος", icon: SlidersHorizontal },
    { key: "prints", label: "Εκτυπώσεις & Πρότυπα", icon: Printer },
    { key: "data", label: "Backup & Δεδομένα", icon: Database },
    { key: "tools", label: "FTEXT & Logs", icon: Terminal },
  ];

  const doRaw = async () => {
    setBusy(true);
    try {
      const r = await http.post("/ftext/raw", { command_text: raw });
      setResult(r.data.log);
      if (r.data.ok) toast.success("Εστάλη");
      else toast.error(r.data.result?.result_description);
    } catch (e) {
      toast.error("Σφάλμα");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="px-6 py-4 border-b border-slate-200 bg-grid">
        <h2 className="font-heading text-2xl font-bold">Διαχείριση</h2>
        <div className="text-xs text-slate-500">
          Ρυθμίσεις συστήματος, πρότυπα εκτυπώσεων, backup, δεδομένα & logs
        </div>
        {/* Top-level ενότητες — segmented nav */}
        <div className="mt-3 flex flex-wrap gap-2" data-testid="admin-section-nav">
          {SECTIONS.map((s) => {
            const active = section === s.key;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => setSection(s.key)}
                data-testid={`admin-section-${s.key}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold border-2 transition-all ${
                  active
                    ? "bg-blue-600 text-white border-blue-700 shadow-md"
                    : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50 hover:border-slate-400"
                }`}
              >
                <s.icon size={16} />
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scroll-thin p-6 grid grid-cols-1 gap-4 max-w-6xl">
        {section === "settings" && (
          <Card className="p-4" data-testid="admin-system-settings">
            <h3 className="font-heading text-lg font-semibold">Ρυθμίσεις Συστήματος</h3>
            <p className="text-xs text-slate-500 mb-2">
              Driver, στοιχεία επιχείρησης, εκτυπωτές, σταθμοί, μέθοδοι πληρωμής,
              τμήματα ταμειακής, ζυγός, δικαιώματα, συσκευές.
            </p>
            <SettingsPage group="admin" />
          </Card>
        )}

        {section === "prints" && (
          <>
            <PrintHealthPanel />
            <PrintTemplatesPanel scope="tenant" />
          </>
        )}

        {section === "data" && (
          <>
            <BackupPanel />
            <Card className="p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold flex items-center gap-2">
                    <ShieldCheck size={18} /> Έλεγχος Υγείας Δεδομένων
                  </h2>
                  <p className="text-sm text-slate-600">
                    Σαρώνει όλους τους πίνακες και εντοπίζει εγγραφές με ελλιπή υποχρεωτικά
                    πεδία (orphan rows, αποτυχημένα imports κλπ).
                  </p>
                </div>
                <Button
                  onClick={() => setHealthOpen(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="admin-db-health-btn"
                >
                  <ShieldCheck size={16} className="mr-1.5" /> Έλεγχος
                </Button>
              </div>
            </Card>
          </>
        )}

        {section === "tools" && (
          <>
            <Card className="p-6 space-y-3">
              <h3 className="font-heading text-lg font-semibold">Raw FTEXT Command</h3>
              <p className="text-xs text-slate-500">
                Για προχωρημένους – στείλε raw εντολές απευθείας στον driver.
              </p>
              <textarea
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                className="w-full min-h-[140px] font-mono text-sm border border-slate-200 rounded-md p-3"
                data-testid="raw-textarea"
              />
              <Button
                onClick={doRaw}
                disabled={busy || !raw.trim()}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="raw-send-btn"
              >
                <Send size={14} className="mr-1.5" /> Αποστολή
              </Button>
            </Card>

            {result && (
              <Card className="p-4">
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">
                  Τελευταίο Αποτέλεσμα
                </div>
                <ResultViewer log={result} />
              </Card>
            )}

            <LogsPanelCollapsible open={logsOpen} onToggle={() => setLogsOpen((v) => !v)} />
          </>
        )}
      </div>
      {healthOpen && (
        <DbHealthDialog open={healthOpen} onClose={() => setHealthOpen(false)} />
      )}
    </div>
  );
}

function LogsPanelCollapsible({ open, onToggle }) {
  return (
    <Card className="p-0 overflow-hidden">
      <button
        onClick={onToggle}
        data-testid="admin-logs-toggle"
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <FileText size={16} className="text-slate-500" />
          <span className="text-lg font-bold">Logs Εφαρμογής</span>
          <span className="text-xs text-slate-500 hidden sm:inline">
            (κλικ για να ανοίξει/κλείσει)
          </span>
        </div>
        {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
      </button>
      {open && (
        <div className="border-t border-slate-200" data-testid="admin-logs-body">
          <LogsPanel />
        </div>
      )}
    </Card>
  );
}
